'use client';

import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { ChevronRightIcon, HomeIcon } from '@heroicons/react/24/outline';
import { Link } from '@/components/ui';
import type { BreadcrumbItem } from '@/types/breadcrumbs';

interface BreadcrumbsProps {
  customBreadcrumbs?: BreadcrumbItem[];
  hasCustomBreadcrumbs?: boolean;
}

export default function Breadcrumbs({
  customBreadcrumbs,
  hasCustomBreadcrumbs = false,
}: BreadcrumbsProps) {
  const pathname = usePathname();
  const t = useTranslations('Breadcrumbs');

  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const segments = pathname.split('/').filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [];

    // Segments that should not be clickable because they don't correspond to real pages
    const NON_LINKABLE_SEGMENTS = new Set(['scorecard', 'account']);

    // Always start with home
    breadcrumbs.push({
      label: t('home'),
      href: '/',
    });

    let currentPath = '';

    segments.forEach((segment, index) => {
      currentPath += `/${segment}`;

      // Skip locale segment
      if (index === 0 && ['en-AU', 'zh-TW', 'ja-JP', 'fr-FR'].includes(segment)) {
        return;
      }

      // Skip UUID segment after 'series' (public series pages use /{locale}/series/{id}/{slug})
      if (segments[index - 1] === 'series' && /^[0-9a-f-]{36}$/.test(segment)) {
        return;
      }

      // Decode URL-encoded segments for display
      const decodedSegment = decodeURIComponent(segment);

      // Map segments to readable labels
      const label = getSegmentLabel(decodedSegment, segments, index);

      // Check if this segment should not be linkable
      const isNonLinkable = NON_LINKABLE_SEGMENTS.has(segment);

      breadcrumbs.push({
        label,
        href: index === segments.length - 1 || isNonLinkable ? undefined : currentPath,
        current: index === segments.length - 1,
      });
    });

    return breadcrumbs;
  };

  const getSegmentLabel = (segment: string, segments: string[], index: number): string => {
    // Handle special cases
    switch (segment) {
      case 'admin':
        return t('admin');
      case 'match':
        return t('match');
      case 'league':
        return t('league');
      case 'ranking':
        return t('ranking');
      case 'series':
        return t('series');
      case 'event':
        return t('event');
      case 'draw':
        return t('draw');
      case 'user':
        return t('profile');
      case 'edit':
        return t('edit');
      case 'new':
        return t('new');
      case 'login':
        return t('login');
      case 'forgot-password':
        return t('forgotPassword');
      case 'reset-password':
        return t('resetPassword');
      case 'private':
        return t('private');
      case 'settings':
        return t('settings');
      case 'profile':
        return t('profile');
      case 'security':
        return t('security');
      case 'preferences':
        return t('preferences');
      case 'payments':
        return t('payments');
      case 'legal':
        return t('legal');
      case 'account':
        return t('account');
      case 'delete':
        return t('delete');
      default:
        // Handle dynamic segments (like user slugs, league slugs, etc.)
        if (segments[index - 1] === 'user') {
          return t('userProfile');
        }
        if (segments[index - 1] === 'club') {
          return t('club');
        }
        if (segment === 'score-choice') {
          return t('scoreChoice');
        }

        // For admin sections
        if (segments[index - 1] === 'admin') {
          switch (segment) {
            case 'league-management':
              return t('leagueManagement');
            case 'user-management':
              return t('userManagement');
            case 'rating-recalculation':
              return t('ratingRecalculation');
            case 'event-management':
              return t('eventManagement');
            case 'location-management':
              return t('locationManagement');
            case 'federation-management':
              return t('federationManagement');
            case 'email-campaigns':
              return t('emailCampaigns');
            case 'series-management':
              return t('seriesManagement');
            case 'box-management':
              return t('boxManagement');
            case 'admin-logs':
              return t('adminLogs');
            default:
              return segment;
          }
        }
        // For location/user management detail pages, show a readable label instead of UUID
        if (segments[index - 1] === 'location-management') {
          return t('locationDetails');
        }
        if (segments[index - 1] === 'user-management') {
          return t('playerDetails');
        }
        // For series slug segment (after the skipped UUID), show as readable name
        if (segments[index - 2] === 'series') {
          return decodeURIComponent(segment).replace(/-/g, ' ');
        }
        // For event segments, return the decoded segment as-is since it's likely a Chinese event name
        if (segments[index - 1] === 'event') {
          return segment;
        }
        // For draw segments, return the decoded segment as-is
        if (segments[index - 1] === 'draw') {
          return segment;
        }
        // For other dynamic segments, return the decoded segment
        return segment;
    }
  };

  // Check if we're on an event page that should have custom breadcrumbs
  const isEventPage = pathname.includes('/event/') && pathname.split('/').length > 4;
  // A scorecard sets its own trail too; the default one would end in the match's raw id.
  const expectsCustom = isEventPage || pathname.includes('/match/scorecard/');

  // Use custom breadcrumbs if provided, otherwise use default only if no custom breadcrumbs are expected
  // For these pages, wait for custom breadcrumbs to avoid showing default ones first
  const breadcrumbs =
    customBreadcrumbs || (!hasCustomBreadcrumbs && !expectsCustom ? generateBreadcrumbs() : []);

  // Don't show breadcrumbs on home page
  if (breadcrumbs.length <= 1) {
    return null;
  }

  // dont show for score page ending with /score or has /score/ in the path
  const pathnameWithoutQueryParams = pathname.split('?')[0];
  if (
    pathnameWithoutQueryParams.endsWith('/score') ||
    pathnameWithoutQueryParams.includes('/score/')
  ) {
    return null;
  }

  return (
    <nav
      className="text-muted my-4 hidden items-center space-x-1 text-sm sm:flex"
      aria-label="Breadcrumb"
    >
      {breadcrumbs.map((breadcrumb, index) => (
        <div key={breadcrumb.href || index} className="flex items-center">
          {index === 0 ? (
            <Link href={breadcrumb.href!} variant="default" color="default">
              <HomeIcon className="h-4 w-4" />
            </Link>
          ) : (
            <>
              <ChevronRightIcon className="mx-1 h-4 w-4" />
              {breadcrumb.current || !breadcrumb.href ? (
                <span
                  className={breadcrumb.current ? 'text-foreground font-medium' : 'text-muted'}
                  aria-current={breadcrumb.current ? 'page' : undefined}
                >
                  {breadcrumb.label}
                </span>
              ) : (
                <Link href={breadcrumb.href} variant="default" color="default">
                  {breadcrumb.label}
                </Link>
              )}
            </>
          )}
        </div>
      ))}
    </nav>
  );
}
